import { concatLatestFrom } from '@ngrx/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, mergeMap, repeat, switchMap, tap } from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import dayjs from 'dayjs';
import { ProjectApiService } from '../../services/project-api.service';
import { NotificationsService } from '../../services/notifications.service';
import { CurrentUserService } from '../../services/current-user.service';
import { ProjectSpendService } from '../../services/project-spend.service';
import { commitmentsActions } from '../commitments/commitments.actions';
import { itemTrackingActions } from '../item-tracking/item-tracking.actions';
import { loadSpends, setProjectStartDate, spendActionTypes } from '../spend/spend.actions';
import { AppState } from '../app-state/index';
import {
  FREE_SP_ALL_PROJECT_VIEWS,
  MANAGER_ALL_PROJECT_VIEWS,
  PAID_SP_ALL_PROJECT_VIEWS,
  PROJECT_VIEWS,
  VIEW_PROJECT_REFRESH_CAUSE,
} from '../../framework/constants/view-project.constants';
import { ProjectDescriptionsService } from '../../services/project-descriptions.service';
import {
  ProjectDescriptionErrorResponse,
  ProjectDescriptionPayload,
} from '../../pages/webapp/projects/view-project/project-updates/project-updates.types';
import { ReportingService } from '../../services/reporting.service';
import { templatesActions } from '../templates/templates.actions';
import { viewProjectSelectors } from './view-project.selectors';
import { viewProjectActions } from './view-project.actions';
import { activitiesActions } from '@app/store/activities/activities.actions';
import { ChangeActionsStore } from '@app/store/change-actions/change-actions.store';
import { TeamManagementService } from '@app/services/team-management.service';
import { InvitesApiService } from '@app/services/invites-api.service';
import { isOneOf } from '@app/framework/helpers/general.helper';

@Injectable()
export class ViewProjectEffects {
  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private projectService: ProjectApiService,
    private notif: NotificationsService,
    private userService: CurrentUserService,
    private spendService: ProjectSpendService,
    private descriptionsService: ProjectDescriptionsService,
    private reportService: ReportingService,
    private changeActionsStore: ChangeActionsStore,
    private teamsApiService: TeamManagementService,
    private invitesApiService: InvitesApiService,
  ) {}

  // loadProject$ = createEffect(() =>
  //   this.actions.pipe(
  //     ofType(viewProjectActions.selectedProjectChanged),
  //     withLatestFrom(this.store.select(viewProjectSelectors.getViewProjectState)),
  //     switchMap(([action, state]) => {
  //       this.store.dispatch(viewProjectActions.startedLoading());
  //       return this.projectService
  //         .getProjectWithObservable(action.projectId)
  //         .pipe(map((project) => viewProjectActions.projectLoaded({ project })));
  //     }),
  //     catchError((error) => {
  //       this.notif.showError(error?.message || 'An error occurred while loading the project');
  //       return of(viewProjectActions.cancel());
  //     }),
  //   ),
  // );

  private validateView(view: PROJECT_VIEWS): boolean {
    const isManager = this.userService.isManager;
    const isPaidSP = this.userService.isPremiumSP;
    const isFreeSP = this.userService.isFreeSP;

    if (!view) {
      return false;
    }

    if (
      isManager &&
      isOneOf(
        view,
        MANAGER_ALL_PROJECT_VIEWS.map((v) => v.data),
      )
    ) {
      return true;
    }

    if (
      isPaidSP &&
      isOneOf(
        view,
        PAID_SP_ALL_PROJECT_VIEWS.map((v) => v.data),
      )
    ) {
      return true;
    }

    if (
      isFreeSP &&
      isOneOf(
        view,
        FREE_SP_ALL_PROJECT_VIEWS.map((v) => v.data),
      )
    ) {
      return true;
    }

    if (view === PROJECT_VIEWS.DEFAULT) {
      return true;
    }

    return false;
  }

  viewChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.viewChanged),
      // the use of switchMap here is intentional, as concatLatestFrom caused weird bugs because it did not wait for userService.data$ to emit a value, and the stream became dead.
      switchMap((action) => this.userService.data$.pipe(map((user) => ({ action, user })))),
      map(({ action, user }) => {
        let view = action.view;
        if (!this.validateView(view)) {
          view = PROJECT_VIEWS.DEFAULT;
        }

        if (view === PROJECT_VIEWS.DEFAULT) {
          view = user.isPaidAccount ? PROJECT_VIEWS.PROJECT_SPEND : PROJECT_VIEWS.COMMITMENTS;
        }

        this.reportService.setSelectedProjectView(view);
        return viewProjectActions.changeView({ view });
      }),
    );
  });

  loadTemplateByProperty$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.loadTemplatesByProperty),
      exhaustMap((action) => {
        // this is available only for managers as GCs don't have properties
        return this.projectService.getBudgetTemplatesManager(action.propertyId, true).pipe(
          map((templates) => {
            return viewProjectActions.projectTemplatesLoaded({ templates });
          }),
        );
      }),
    );
  });

  refreshProjectData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.refreshNeeded, viewProjectActions.selectedProjectChanged),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      mergeMap(([action, state]) => {
        const projectId = (action as any).projectId ?? state.currentProjectId;
        this.store.dispatch(viewProjectActions.startedLoading({}));
        this.store.dispatch(commitmentsActions.projectChanged({ projectId }));

        if (!this.userService.isFreeSP) {
          this.store.dispatch(viewProjectActions.loadProjectTeamMembers({ projectId }));
          this.store.dispatch(viewProjectActions.loadProjectInviteMembers({ projectId }));
        }

        if ('cause' in action && action.cause === VIEW_PROJECT_REFRESH_CAUSE.SPEND_SET_ALL) {
          // refresh only the line items, the other data is already loaded
          this.store.dispatch(viewProjectActions.loadLineItems({ projectId }));
          return of(viewProjectActions.cancel());
        }

        if (this.userService.isPaidAccount) {
          this.store.dispatch(itemTrackingActions.setActiveProject({ projectId }));
          this.store.dispatch(loadSpends({ projectId }));
          if (this.userService.isPremiumSP) {
            this.store.dispatch(viewProjectActions.loadSpendsForGc({ projectId }));
            // this is only needed to disable/enable the PCCO button - only one COR is enough
            this.changeActionsStore.loadCORs(true, true);
            // this is needed to disable adding a COR if there's no PCO
            this.changeActionsStore.loadPCOs(true, true);
          } else {
            this.store.dispatch(viewProjectActions.loadSpendsForMg({ projectId }));
            this.store.dispatch(viewProjectActions.loadLineItems({ projectId }));
          }
        }

        return this.projectService.getProjectWithObservable(state.currentProjectId).pipe(
          catchError((error) => {
            console.warn('Error loading project', error?.error?.message);
            this.notif.showError(
              error?.error?.message || 'An error occurred while loading the project',
            );
            return of(null);
          }),
          tap((project) => {
            if (!project) {
              return;
            }

            if (this.userService.isManager) {
              this.store.dispatch(
                viewProjectActions.loadTemplatesByProperty({ propertyId: project.property_id }),
              );
            } else if (this.userService.isPremiumSP) {
              this.store.dispatch(templatesActions.loadBudgetTemplate());
            }

            this.store.dispatch(
              commitmentsActions.setCommitmentProjectData({
                projectData: {
                  id: project.id,
                  start_date: project.start_date,
                },
              }),
            );

            if (this.userService.isPaidAccount) {
              this.store.dispatch(
                setProjectStartDate({
                  projectStartDate: dayjs.utc(project.start_date).format('YYYY-MM-DD'),
                }),
              );
              this.store.dispatch(
                spendActionTypes.setHasProjectTemplateFromViewProject({
                  hasTemplates: !!project?.budget_template_id,
                }),
              );
            }
          }),
          map((project) => {
            if (!project) {
              return viewProjectActions.cancel();
            }
            this.notif.close();
            return viewProjectActions.projectLoaded({ project });
          }),
        );
      }),
    );
  });

  onDestroy$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(viewProjectActions.onDestroy),
        tap(() => {
          this.changeActionsStore.onProjectDestroy();
        }),
      );
    },
    { dispatch: false },
  );

  loadSpendsForMG$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.loadSpendsForMg),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      switchMap(([action, state]) => {
        this.store.dispatch(viewProjectActions.startedLoading({ target: 'spends' }));
        const projectId = action.projectId ?? state.currentProjectId;
        return this.spendService.getAllWithObservable(projectId).pipe(
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while fetching spend data.',
            );
            this.store.dispatch(viewProjectActions.loadingEnded({ target: 'spends' }));
            console.warn('get all spends for overview error', error);
            return of(undefined);
          }),
          map((spends) => {
            if (!spends) {
              return viewProjectActions.cancel();
            }
            return viewProjectActions.spendsLoadedForMg({ projectId, spends });
          }),
        );
      }),
    );
  });

  loadSpendsForGc$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.loadSpendsForGc),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      switchMap(([action, state]) => {
        this.store.dispatch(viewProjectActions.startedLoading({ target: 'spends' }));
        const projectId = action.projectId ?? state.currentProjectId;
        return this.spendService.getAllGc(projectId).pipe(
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while fetching spend data.',
            );
            this.store.dispatch(viewProjectActions.loadingEnded({ target: 'spends' }));
            return of(undefined);
          }),
          map((spends) => {
            if (!spends) {
              return viewProjectActions.cancel();
            }
            return viewProjectActions.spendsLoadedForGc({ projectId, spends });
          }),
        );
      }),
    );
  });

  DescriptionsSaved$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          viewProjectActions.reloadProjectDescriptions,
          viewProjectActions.projectDescriptionsSuccessfullySaved,
        ),
        tap((_) => this.notif.showSuccess('Successfully saved!')),
      );
    },
    { dispatch: false },
  );

  loadProjectDescriptions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        viewProjectActions.loadProjectDescriptions,
        viewProjectActions.reloadProjectDescriptions,
      ),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      switchMap(([action, state]) => {
        const projectId = action.projectId ?? state.currentProjectId;
        return this.descriptionsService.getProjectDescriptions(projectId).pipe(
          map((descriptions) => {
            return viewProjectActions.projectDescriptionsLoaded({
              projectDescriptions: descriptions,
              projectId: state.currentProjectId,
            });
          }),
        );
      }),
      catchError((error) => {
        this.notif.showError(
          error?.error?.message || 'An error occurred while project loading updates.',
        );
        console.warn('project loading data err', error);
        return of(viewProjectActions.cancel());
      }),
      repeat(),
    );
  });

  saveProjectDescriptionsAsync$ = createEffect(() => {
    // not the greatest architecture of the world... But we have to cook with the ingredients we have.
    // todo: refactor this whole messy thing and retry failed responses and update store with the OK responses
    return this.actions$.pipe(
      ofType(viewProjectActions.saveProjectDescriptionsAsync),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      mergeMap(([action, state]) => {
        const updateObservables = action.updatedDescriptions.map((description) => {
          const payload: Partial<ProjectDescriptionPayload> = {
            ...description,
            project_id: state.currentProjectId,
          };
          return this.descriptionsService.updateProjectDescription(payload).pipe(
            catchError((error): Observable<ProjectDescriptionErrorResponse> => {
              return of({
                error,
                description,
                type: 'update',
              });
            }),
          );
        });

        const createObservables = action.newDescriptions.map((description) => {
          const payload: ProjectDescriptionPayload = {
            ...description,
            project_id: state.currentProjectId,
          };
          delete payload.id;
          return this.descriptionsService.createProjectDescription(payload).pipe(
            catchError((error): Observable<ProjectDescriptionErrorResponse> => {
              return of({
                error,
                description,
                type: 'create',
              });
            }),
          );
        });

        const deleteObservables = action.deletedIds.map((id) => {
          return this.descriptionsService
            .deleteProjectDescription(id, {
              project_id: state.currentProjectId,
            })
            .pipe(
              catchError((error): Observable<ProjectDescriptionErrorResponse> => {
                return of({
                  error,
                  description: { id },
                  type: 'delete',
                });
              }),
            );
        });

        return forkJoin([...updateObservables, ...createObservables, ...deleteObservables]).pipe(
          map((responses) => {
            const errorResponses = responses.filter((resp) => resp.error);
            if (errorResponses.length > 0) {
              console.warn('Some responses have errors', errorResponses);
              this.notif.showError(
                `Couldn't save all project descriptions. ${errorResponses[0]?.error?.message || ''}`,
              );
            }
            return viewProjectActions.reloadProjectDescriptions({
              projectId: state.currentProjectId,
            });
          }),
        );
      }),
      repeat(),
    );
  });

  loadLineItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.loadLineItems),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.getViewProjectState)),
      switchMap(([action, state]) => {
        this.store.dispatch(viewProjectActions.startedLoading({ target: 'lineItems' }));
        const projectId = action.projectId ?? state.currentProjectId;
        return this.spendService.getLineItemsByProjectId(projectId, true).pipe(
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while fetching line items.',
            );
            this.store.dispatch(viewProjectActions.loadingEnded({ target: 'lineItems' }));
            console.warn('get all line items for overview error', error);
            return of(undefined);
          }),
          map((lineItems) => {
            if (!lineItems) {
              return viewProjectActions.cancel();
            }
            return viewProjectActions.lineItemsLoaded({ projectId, lineItems });
          }),
        );
      }),
    );
  });

  loadLineItemsSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.projectLoaded, activitiesActions.activitySelected),
      concatLatestFrom(() => [
        this.store.select(viewProjectSelectors.selectProjectId),
        this.store.select(viewProjectSelectors.getSelectedProject),
      ]),
      switchMap(([action, projectId, selectedProject]) => {
        if (action.type === activitiesActions.activitySelected.type) {
          projectId = action.activity?.project?.id;
        }

        // fallback value if selected project is not available
        let onlySubLines = this.userService.isPremiumSP;
        if (selectedProject?.user?.type?.name) {
          onlySubLines = selectedProject.user.type.name !== 'manager';
        }

        return this.spendService.getLineItemsSummary(projectId, onlySubLines).pipe(
          map((lineItemsSummary) => {
            return viewProjectActions.lineItemsSummaryLoaded({ lineItemsSummary, projectId });
          }),
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while fetching line items summary.',
            );
            console.warn('get all line items summary for overview error', error);
            return EMPTY;
          }),
        );
      }),
    );
  });

  loadForecastModifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.loadForecastModifications),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([_, projectId]) => {
        return this.projectService.getForecastModificationsPerBudgetLine(projectId).pipe(
          map((forecastModifications) => {
            return viewProjectActions.forecastModificationsLoaded({
              forecastModifications,
              projectId,
            });
          }),
          catchError((error) => {
            this.notif.showError(
              error?.error?.message || 'An error occurred while fetching anticipated costs.',
            );
            return of(viewProjectActions.forecastModificationsLoadFailed({ projectId }));
          }),
        );
      }),
    );
  });

  loadProjectTeamMembers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.loadProjectTeamMembers),
      switchMap((action) => {
        return this.teamsApiService.getTeamMembers([action.projectId]);
      }),
      map((response) => {
        return viewProjectActions.loadProjectTeamMembersSuccess({ users: response });
      }),
    );
  });

  loadProjectInviteMembers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(viewProjectActions.loadProjectInviteMembers),
      switchMap((action) => {
        return this.invitesApiService.getUsersInvitedToProject(action.projectId);
      }),
      map((response) => {
        return viewProjectActions.loadProjectInviteMembersSuccess({ users: response });
      }),
    );
  });
}
